import { useEffect, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { FileText, Hash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LangBadge } from '@/components/ui/badge'
import { EmptyState, Skeleton } from '@/components/ui/misc'
import { cn, formatNumber, highlightMatch } from '@/lib/utils'
import type { Term } from '@/types/terms'

const ROW_HEIGHT = 56

interface TermListProps {
  terms: Term[]
  isLoading: boolean
  query: string
  selected: Term | null
  onSelect: (t: Term) => void
}

/**
 * Virtualised list of the whole layer. Rendering 34k rows directly would lock
 * the browser, so only the visible window is mounted — this is what lets us
 * drop the old 1000-row cap entirely.
 */
export function TermList({ terms, isLoading, query, selected, onSelect }: TermListProps) {
  const { t } = useTranslation()
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: terms.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  })

  const selectedIndex = selected
    ? terms.findIndex((t) => t.term_id === selected.term_id)
    : -1

  // Keep the selected row in view when selection changes from outside the list
  // (command palette, similar-term chip, restored URL).
  useEffect(() => {
    if (selectedIndex >= 0) virtualizer.scrollToIndex(selectedIndex, { align: 'auto' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex])

  // j/k and arrow keys move through the list without leaving the keyboard.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      const isNext = e.key === 'j' || e.key === 'ArrowDown'
      const isPrev = e.key === 'k' || e.key === 'ArrowUp'
      if (!isNext && !isPrev) return
      e.preventDefault()
      const next = Math.min(
        terms.length - 1,
        Math.max(0, (selectedIndex < 0 ? -1 : selectedIndex) + (isNext ? 1 : -1)),
      )
      if (terms[next]) onSelect(terms[next])
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [terms, selectedIndex, onSelect])

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1 p-2">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (!terms.length) {
    return (
      <EmptyState title={t('terms.listEmpty')} hint={t('terms.listEmptyHint')} />
    )
  }

  return (
    <div ref={parentRef} className="scrollbar-thin h-full overflow-y-auto">
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((row) => {
          const term = terms[row.index]
          const isSelected = term.term_id === selected?.term_id
          return (
            <button
              key={term.term_id}
              type="button"
              onClick={() => onSelect(term)}
              className={cn(
                'absolute left-0 top-0 flex w-full items-center gap-2.5 border-b border-border/60 px-3 text-left transition-colors',
                isSelected ? 'bg-accent' : 'hover:bg-muted',
              )}
              style={{ height: row.size, transform: `translateY(${row.start}px)` }}
            >
              <LangBadge lang={term.lang} />
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    'truncate text-sm',
                    isSelected ? 'font-semibold text-accent-foreground' : 'font-medium',
                  )}
                >
                  {highlightMatch(term.value, query).map((seg, i) =>
                    seg.match ? (
                      <mark
                        key={i}
                        className="rounded-sm bg-highlight px-0.5 text-highlight-foreground"
                      >
                        {seg.text}
                      </mark>
                    ) : (
                      <span key={i}>{seg.text}</span>
                    ),
                  )}
                  {term.term_type === 'abbreviation' && (
                    <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
                      {t('terms.abbreviationShort')}
                    </span>
                  )}
                </div>
                {term.domain && (
                  <div className="truncate text-[11px] text-muted-foreground">{term.domain}</div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2.5 text-[11px] tabular-nums text-muted-foreground">
                <span className="flex items-center gap-1" title={t('terms.frequency')}>
                  <Hash className="size-3" />
                  {formatNumber(term.freq)}
                </span>
                <span className="flex items-center gap-1" title={t('terms.documentCount')}>
                  <FileText className="size-3" />
                  {formatNumber(term.doc_count)}
                </span>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
